import React, { useEffect, useState } from 'react';
import {
  Box,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import ReviewEntityMetricsTrending from '../../components/ReviewEntityMetricsTrending/ReviewEntityMetricsTrending';
import ProjectPortfolioMetricsDonuts from './ProjectPortfolioMetricsDonuts';
import { getLeafProjectEntities, LeafProjectEntityOption } from '../../services/api';

/**
 * Dashboard metrics: Aggregated vs Parent Level toggle was removed while that behavior is under discussion.
 * To reinstate: restore useState for metricsViewMode, ToggleButtonGroup for !isLeafEntity, and
 * `includeAggregate = isLeafEntity ? true : metricsViewMode === 'aggregated'` (see git history).
 */

interface MetricsTabProps {
  parentEntityId: number;
  childEntityTypes?: any[];
  isLeafEntity?: boolean;
  entityTypeId?: number | null;
  /** Category from "View By Category" (e.g. PROJECT); drives portfolio distribution sub-tab. */
  selectedCategory?: string | null;
}

const MetricsTab: React.FC<MetricsTabProps> = ({
  parentEntityId,
  isLeafEntity = false,
  selectedCategory = null,
}) => {
  const includeAggregate = true;

  const showPortfolioTab =
    !isLeafEntity && (selectedCategory || '').toUpperCase() === 'PROJECT';

  const [metricsViewSubTab, setMetricsViewSubTab] = useState(0);
  const [leafProjects, setLeafProjects] = useState<LeafProjectEntityOption[]>([]);
  const [leafProjectsLoading, setLeafProjectsLoading] = useState(false);
  const [leafProjectsError, setLeafProjectsError] = useState<string | null>(null);
  const [selectedLeafProjectId, setSelectedLeafProjectId] = useState<number | null>(null);

  useEffect(() => {
    if (!showPortfolioTab) {
      setMetricsViewSubTab(0);
    }
  }, [showPortfolioTab, parentEntityId]);

  useEffect(() => {
    if (!showPortfolioTab) {
      setLeafProjects([]);
      setLeafProjectsError(null);
      setSelectedLeafProjectId(null);
      return;
    }

    let cancelled = false;
    const load = async () => {
      setLeafProjectsLoading(true);
      setLeafProjectsError(null);
      try {
        const res = await getLeafProjectEntities(parentEntityId);
        if (cancelled) return;
        const list = res.data.entities || [];
        setLeafProjects(list);
        setSelectedLeafProjectId(list[0]?.id ?? null);
      } catch (e: unknown) {
        const err = e as { response?: { data?: { detail?: string } }; message?: string };
        if (!cancelled) {
          setLeafProjects([]);
          setSelectedLeafProjectId(null);
          setLeafProjectsError(
            err.response?.data?.detail || err.message || 'Failed to load leaf projects'
          );
        }
      } finally {
        if (!cancelled) {
          setLeafProjectsLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [showPortfolioTab, parentEntityId]);

  const projectLevelMetricsContent = () => {
    if (leafProjectsLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      );
    }
    if (leafProjectsError) {
      return (
        <Alert severity="error" sx={{ mb: 2 }}>
          {leafProjectsError}
        </Alert>
      );
    }
    if (leafProjects.length === 0) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          No leaf <strong>PROJECT</strong> entities were found under this selection. Metrics are loaded for leaf
          projects only—expand the hierarchy or pick an entity that has project leaves.
        </Alert>
      );
    }

    return (
      <>
        <FormControl fullWidth size="small" sx={{ mb: 2, maxWidth: 480 }}>
          <InputLabel id="leaf-project-metrics-select">Leaf project</InputLabel>
          <Select
            labelId="leaf-project-metrics-select"
            label="Leaf project"
            value={selectedLeafProjectId ?? ''}
            onChange={(e) => setSelectedLeafProjectId(Number(e.target.value))}
          >
            {leafProjects.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.entity_type_display_label
                  ? `${p.name} (${p.entity_type_display_label})`
                  : p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
          Showing metrics for the selected leaf project. Use Metric level to view the whole project
          (default) or drill into module, team, sprint, and other configured grains.
        </Typography>
        {selectedLeafProjectId != null && (
          <ReviewEntityMetricsTrending
            reviewEntityId={selectedLeafProjectId}
            includeAggregate={includeAggregate}
            showEntityNavigation={false}
            showSelectEntityPrompt={false}
          />
        )}
      </>
    );
  };

  return (
    <Box>
      {showPortfolioTab && (
        <Tabs
          value={metricsViewSubTab}
          onChange={(_, v) => setMetricsViewSubTab(v)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Project level metrics" sx={{ textTransform: 'none' }} />
          <Tab label="Portfolio distribution" sx={{ textTransform: 'none' }} />
        </Tabs>
      )}

      {!showPortfolioTab && (
        <ReviewEntityMetricsTrending
          reviewEntityId={parentEntityId}
          includeAggregate={includeAggregate}
          showEntityNavigation={false}
          showSelectEntityPrompt={false}
        />
      )}

      {showPortfolioTab && metricsViewSubTab === 0 && projectLevelMetricsContent()}

      {showPortfolioTab && metricsViewSubTab === 1 && (
        <ProjectPortfolioMetricsDonuts parentEntityId={parentEntityId} />
      )}
    </Box>
  );
};

export default MetricsTab;
